import { Badge, Button, Flex, IconButton, Menu, MenuButton, MenuItem, MenuList, useToast } from "@chakra-ui/react";
import * as React from "react";
import { useEffect, useState } from "react";
import { Plus, X } from "@phosphor-icons/react";
import { useGetUsers } from "../../../../hooks/query/user-administration";
import { constructQueryParamsString, showErrorToast } from "../../../../utils";
import { issbKeys, useChangeAssignee, useChangeOwner } from "../../../../hooks/query";
import { hideAppSpinner, showAppSpinner } from "../../../../context/Store/slices/appSpinnerSlice";
import { useDispatch } from "react-redux";
import { useQueryClient } from "react-query";
import { MULTIPLE_ANSWERS_MARKER } from "../../IssbDataInput/components/tools/BulkActions";
import useAirUserRoles from "../../../../utils/AuthUtils";

/**
 * ?IssbQuestion[] questions Hack: Optional for updating the immeadiate state.
 * This provides a workaround for the lose-other-edits bug (Jira ESG-2678) vs not-showing-updates-without-a-reload
 */
const PeopleTag = ({ type, users, questionIds, alignment = "row", questions, readOnly }) => {
  const toast = useToast();
  const [userList, setUserList] = useState([]);

  const getUsersQueryParamsString = () => {
    return constructQueryParamsString({
      page: 0,
      size: 1000,
    });
  };

  useGetUsers(getUsersQueryParamsString(), {
    onSuccess: (data) => {
      const users =
        data.page?.content?.map((user) => {
          return {
            value: user?.email,
            label: user?.email,
          };
        }) ?? [];
      setUserList(users);
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0,
  });

  return (
    <Flex gap={2} width="100%">
      {type === "assignee" ? (
        <>
          <AssigneeTag
            userList={userList}
            alignment={alignment}
            assignees={users}
            questionIds={questionIds}
            questions={questions}
            readOnly={readOnly}
          />
        </>
      ) : null}

      {type === "owner" ? (
        <OwnerTag
          owner={users?.[0]}
          userList={userList}
          questionIds={questionIds}
          questions={questions}
          readOnly={readOnly}
        />
      ) : null}
    </Flex>
  );
}; // ./PeopleTag

const OwnerTag = ({ owner, userList, questionIds, questions, readOnly }) => {
  const [localOwner, setLocalOwner] = useState(owner || "No");
  const dispatch = useDispatch();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { isController } = useAirUserRoles();

  useEffect(() => {
    if (owner) setLocalOwner(owner);
  }, [owner]);

  const { mutate: changeOwner } = useChangeOwner({
    onMutate: () => dispatch(showAppSpinner()),
    onError: (err) => {
      showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message);
      setLocalOwner(owner); // revert immeadiate UI feedback
    },
    onCancel: () => {
      setLocalOwner(owner); // revert immeadiate UI feedback
    },
    onSettled: () => dispatch(hideAppSpinner()),
    onSuccess: (foo) => {
      if (questionIds.length > 1) {
        // see lose-other-edits bug, Jira ESG-2678
        queryClient.invalidateQueries(issbKeys.answer());
      }
    },
  });

  const handleChangeOwner = (newOwner) => {
    setLocalOwner(newOwner); // immeadiate UI feedback + workaround re ESG-2678
    const dataToSubmit = {
      questionIds: questionIds,
      user: newOwner,
    };
    changeOwner(dataToSubmit);
    questions?.forEach((question) => {
      question.owner = newOwner;
    });
  };

  return (
    <Flex gap={2} wrap="wrap">
      <Menu>
        <MenuButton
          as={Badge}
          fontSize="body.xxs"
          width="fit-content"
          px={2}
          py={1}
          borderRadius="md"
          colorScheme="orange"
          _hover={{
            cursor: "pointer",
          }}
        >
          {localOwner}
        </MenuButton>
        {/* Only controller can change owner */}
        {isController() && !readOnly && (
          <MenuList maxHeight="250px" overflowY="auto">
            {userList?.map((user) => (
              <MenuItem fontSize="body.xs" key={user.value} onClick={() => handleChangeOwner(user.value)}>
                {user.label}
              </MenuItem>
            ))}
          </MenuList>
        )}
      </Menu>
    </Flex>
  );
}; //./OwnerTag

const AssigneeTag = ({ userList, assignees, questionIds, alignment, questions, readOnly }) => {
  const [localAssignees, setLocalAssignees] = useState(assignees || []);

  const dispatch = useDispatch();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { isOwner } = useAirUserRoles();

  // check if the user is the owner of all the questions
  const isOwnerOfQuestions = () => {
    return questionIds?.every((qId) => isOwner(qId));
  };

  useEffect(() => {
    if (assignees) setLocalAssignees(assignees);
  }, [assignees]);

  const { mutate: _changeAssignee } = useChangeAssignee({
    onMutate: () => dispatch(showAppSpinner()),
    onError: (err) => {
      showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message);
      setLocalAssignees(assignees); // revert immeadiate UI feedback
    },
    onSettled: () => dispatch(hideAppSpinner()),
    onCancel: () => {
      setLocalAssignees(assignees);
    },
    onSuccess: () => {
      if (questionIds.length > 1) {
        // see lose-other-edits bug, Jira ESG-2678
        queryClient.invalidateQueries(issbKeys.answer());
      }
    },
  });

  const changeAssignee = (dataToSubmit) => {
    // call API
    _changeAssignee(dataToSubmit);
    // local state (workaround for lose-other-edits bug, Jira ESG-2678)
    let newAssignees = localAssignees;
    if (!newAssignees || newAssignees === MULTIPLE_ANSWERS_MARKER) {
      newAssignees = [];
    }
    if (dataToSubmit.action === "ASSIGN") {
      newAssignees = newAssignees.concat(dataToSubmit.user);
    } else {
      newAssignees = newAssignees.filter((user) => user !== dataToSubmit.user);
    }
    setLocalAssignees(newAssignees);
    questions?.forEach((question) => {
      question.assignees = newAssignees;
    });
  };

  const handleAddAssignee = (user) => {
    const dataToSubmit = {
      questionIds: questionIds,
      user: user,
      action: "ASSIGN",
    };
    changeAssignee(dataToSubmit);
  };

  const handleRemoveAssignee = (user) => {
    const dataToSubmit = {
      questionIds: questionIds,
      user: user,
      action: "UNASSIGN",
    };
    changeAssignee(dataToSubmit);
  };

  // for tidyness, dont list users that are already assigned
  const userListFiltered = Array.isArray(localAssignees)
    ? userList.filter((user) => !localAssignees.includes(user.value))
    : userList;

  return (
    <Flex direction={alignment} gap={2} wrap="wrap">
      {isOwnerOfQuestions() && !readOnly && (
        <AddButton userList={userListFiltered} questionIds={questionIds} onAdd={handleAddAssignee} />
      )}
      {localAssignees !== MULTIPLE_ANSWERS_MARKER &&
        localAssignees?.map((user) => (
          <Badge
            px={2}
            py={1}
            borderRadius="md"
            fontSize="body.xxs"
            colorScheme="yellow"
            width="fit-content"
            key={user}
          >
            {user}
            <IconButton
              variant="plain"
              size="xxs"
              ml={1}
              // icon={}
              aria-label="Unassign"
            >
              {isOwnerOfQuestions() && !readOnly && (
                <DeleteAssigneeButton questionIds={questionIds} user={user} onRemove={handleRemoveAssignee} />
              )}
            </IconButton>
          </Badge>
        ))}
    </Flex>
  );
};

const AddButton = ({ userList, questionIds, onAdd }) => {
  return (
    <>
      <Menu>
        <MenuButton
          as={Button}
          size="xs"
          varient="outline"
          width="fit-content"
          rightIcon={<Plus size={14} weight="bold" />}
        >
          Add
        </MenuButton>
        <MenuList maxHeight="250px" overflowY="auto">
          {userList?.map((user) => (
            <MenuItem fontSize="body.xs" key={user.value} onClick={() => onAdd(user.value)}>
              {user.label}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </>
  );
};

const DeleteAssigneeButton = ({ questionIds, user, onRemove }) => {
  return <X size={12} weight="bold" onClick={() => onRemove(user)} />;
};

export default PeopleTag;
